import React, { useState } from 'react';
import { Plus, Edit3, Trash2, User, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Card, CardContent } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const ManageProfiles = ({ onClose }) => {
  const [profiles, setProfiles] = useState([
    { 
      id: 1, 
      name: "John", 
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=John",
      isMain: true,
      type: "Adult"
    },
    { 
      id: 2, 
      name: "Sarah", 
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah",
      isMain: false,
      type: "Adult"
    },
    { 
      id: 3, 
      name: "Kids", 
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Kids",
      isMain: false,
      type: "Kids"
    }
  ]);

  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [newProfile, setNewProfile] = useState({
    name: '',
    type: 'Adult',
    avatar: ''
  });

  const handleCreateProfile = () => {
    if (newProfile.name.trim()) {
      const profileData = {
        id: Date.now(),
        name: newProfile.name,
        avatar: newProfile.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${newProfile.name}`,
        isMain: false,
        type: newProfile.type
      };
      
      setProfiles(prev => [...prev, profileData]);
      setNewProfile({ name: '', type: 'Adult', avatar: '' });
      setShowCreateProfile(false);
    }
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setNewProfile({
      name: profile.name,
      type: profile.type,
      avatar: profile.avatar
    });
    setShowCreateProfile(true);
  };

  const handleUpdateProfile = () => {
    if (newProfile.name.trim()) {
      setProfiles(prev => prev.map(profile => 
        profile.id === editingProfile.id 
          ? { ...profile, name: newProfile.name, type: newProfile.type, avatar: newProfile.avatar }
          : profile
      ));
      setEditingProfile(null);
      setNewProfile({ name: '', type: 'Adult', avatar: '' });
      setShowCreateProfile(false);
    }
  };

  const handleDeleteProfile = (profileId) => {
    setProfiles(prev => prev.filter(profile => profile.id !== profileId));
  };

  return (
    <div className="space-y-6">
      {/* Existing Profiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {profiles.map((profile) => (
          <Card key={profile.id} className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
            <CardContent className="p-4 text-center">
              <div className="relative mb-3">
                <Avatar className="w-16 h-16 mx-auto">
                  <AvatarImage src={profile.avatar} alt={profile.name} />
                  <AvatarFallback className="bg-gray-700 text-white">
                    {profile.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                {profile.isMain && (
                  <div className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-purple-500 rounded-full p-1">
                    <User className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
              
              <h3 className="text-white font-medium mb-1">{profile.name}</h3>
              <p className="text-gray-400 text-xs mb-3">{profile.type}</p>
              
              <div className="flex gap-2 justify-center">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleEditProfile(profile)}
                  className="text-gray-400 hover:text-white hover:bg-gray-700 p-1 h-8 w-8"
                >
                  <Edit3 className="w-4 h-4" />
                </Button>
                {!profile.isMain && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteProfile(profile.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20 p-1 h-8 w-8"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Add New Profile Card */}
        <Card 
          className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors cursor-pointer border-dashed"
          onClick={() => setShowCreateProfile(true)}
        >
          <CardContent className="p-4 text-center flex flex-col items-center justify-center h-full min-h-[140px]">
            <div className="bg-gradient-to-r from-yellow-400 to-purple-500 rounded-full p-3 mb-3">
              <Plus className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-white font-medium">Add Profile</h3>
            <p className="text-gray-400 text-xs">Create new account</p>
          </CardContent>
        </Card>
      </div>

      {/* Profile Limits Info */}
      <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-blue-400" />
          <div>
            <h4 className="text-white font-medium">Profile Limits</h4>
            <p className="text-gray-400 text-sm">
              You have {profiles.length}/5 profiles. Upgrade to Premium for unlimited profiles.
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onClose} className="border-gray-600 text-gray-300 hover:bg-gray-700">
          Cancel
        </Button>
        <Button 
          onClick={onClose}
          className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-white"
        >
          Done
        </Button>
      </div>

      {/* Create/Edit Profile Modal */}
      <Dialog open={showCreateProfile} onOpenChange={setShowCreateProfile}>
        <DialogContent className="bg-gray-900 text-white border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingProfile ? 'Edit Profile' : 'Create New Profile'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="profileName" className="text-white">Profile Name</Label>
              <Input
                id="profileName"
                value={newProfile.name}
                onChange={(e) => setNewProfile(prev => ({ ...prev, name: e.target.value }))}
                className="bg-gray-800 border-gray-600 text-white focus:border-yellow-400"
                placeholder="Enter profile name"
                maxLength={20}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Profile Type</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={newProfile.type === 'Adult' ? 'default' : 'outline'}
                  onClick={() => setNewProfile(prev => ({ ...prev, type: 'Adult' }))}
                  className={newProfile.type === 'Adult' 
                    ? 'bg-gradient-to-r from-yellow-400 to-purple-500 text-white' 
                    : 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  }
                >
                  Adult Profile
                </Button>
                <Button
                  type="button"
                  variant={newProfile.type === 'Kids' ? 'default' : 'outline'}
                  onClick={() => setNewProfile(prev => ({ ...prev, type: 'Kids' }))}
                  className={newProfile.type === 'Kids' 
                    ? 'bg-gradient-to-r from-yellow-400 to-purple-500 text-white' 
                    : 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  }
                >
                  Kids Profile
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Avatar Preview</Label>
              <div className="flex items-center gap-4">
                <Avatar className="w-12 h-12">
                  <AvatarImage 
                    src={newProfile.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${newProfile.name || 'default'}`} 
                    alt="Profile preview" 
                  />
                  <AvatarFallback className="bg-gray-700 text-white">
                    {newProfile.name.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                  Change Avatar
                </Button>
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCreateProfile(false);
                  setEditingProfile(null);
                  setNewProfile({ name: '', type: 'Adult', avatar: '' });
                }}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={editingProfile ? handleUpdateProfile : handleCreateProfile}
                className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-white"
                disabled={!newProfile.name.trim()}
              >
                {editingProfile ? 'Update Profile' : 'Create Profile'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageProfiles;
